import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Printer, Mail, Check, MapPin, Target, Layers, Sparkles, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MIRROR_PAIRS = [
  { a: 0, b: 5 },
  { a: 1, b: 6 },
  { a: 2, b: 7 },
  { a: 3, b: 8 },
  { a: 4, b: 9 },
];

const Chip = ({ children, tone = "slate", testid }) => {
  const tones = {
    slate: "bg-slate-800/80 border-slate-700 text-slate-200",
    amber: "bg-amber-500/15 border-amber-500/60 text-amber-300",
    emerald: "bg-emerald-500/15 border-emerald-500/50 text-emerald-300",
    cyan: "bg-cyan-500/10 border-cyan-500/50 text-cyan-300",
  };
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center justify-center min-w-[44px] h-11 px-3 rounded-lg border font-mono-digit text-xl font-bold ${tones[tone]}`}
    >
      {children}
    </span>
  );
};

const StepCard = ({ num, title, icon: Icon, children, testid }) => (
  <Card
    data-testid={testid}
    className="bg-[#131c2e]/90 border-slate-800 shadow-xl shadow-black/40 break-inside-avoid print:border-slate-400 print:shadow-none print:bg-white"
  >
    <CardContent className="p-5 sm:p-7 space-y-4">
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-300 font-heading font-bold text-lg print:bg-amber-100 print:text-amber-900 print:border-amber-400">
          {num}
        </span>
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-5 h-5 text-amber-300 print:text-amber-900" />}
          <h3 className="font-heading text-xl sm:text-2xl font-bold text-slate-50 print:text-slate-900">
            {title}
          </h3>
        </div>
      </div>
      <div className="text-slate-300 leading-relaxed print:text-slate-700">
        {children}
      </div>
    </CardContent>
  </Card>
);

export default function HowItWorks() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { email, source: "how-it-works" });
      setSubmitted(true);
      toast.success("You're on the list — thanks!");
    } catch {
      toast.error("Could not submit — please try again.");
    } finally {
      setLoading(false);
    }
  };

  const printPage = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-[#0b0f17] text-slate-100 print:bg-white print:text-slate-900">
      {/* NAV — hidden on print */}
      <div className="print:hidden sticky top-0 z-10 border-b border-slate-800 bg-[#0b0f17]/90 backdrop-blur">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-300 hover:text-amber-300 transition" data-testid="back-to-app">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-heading text-sm font-bold">Back to Hit Path</span>
          </Link>
          <Button
            onClick={printPage}
            variant="outline"
            className="h-9 border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
            data-testid="print-pamphlet-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            Save as PDF / Print
          </Button>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10 sm:py-14 space-y-10 print:py-6">
        {/* COVER / HERO */}
        <header className="text-center space-y-4 print:space-y-2" data-testid="pamphlet-hero">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-amber-300 font-heading font-bold print:text-amber-900">
            <Sparkles className="w-4 h-4" />
            The Pocket Guide
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-slate-50 print:text-slate-900 leading-tight">
            The Hit Path Method
          </h1>
          <p className="text-slate-400 text-base sm:text-lg max-w-2xl mx-auto print:text-slate-700">
            A step-by-step method for working out <strong className="text-slate-200 print:text-slate-900">2, 3 &amp; 4-digit lottery games</strong> — the way The Jersey Fellas do it.
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-sm font-heading font-bold print:bg-emerald-50 print:border-emerald-700 print:text-emerald-900">
            <MapPin className="w-4 h-4" />
            Works in every US state lottery — all 45 jurisdictions
          </div>
        </header>

        {/* WHAT IT IS */}
        <Card className="bg-gradient-to-br from-amber-500/10 via-slate-900/60 to-slate-900/60 border-amber-500/30 print:border-amber-400 print:bg-amber-50">
          <CardContent className="p-6 sm:p-8 space-y-3">
            <h2 className="font-heading text-2xl font-bold text-amber-300 print:text-amber-900">
              What is Hit Path?
            </h2>
            <p className="leading-relaxed text-slate-200 print:text-slate-800">
              Hit Path is a <strong>workout method</strong>, not a prediction tool. You pick a seed (a 2, 3, or 4-digit number), the method rolls each digit through 0–9 to build a grid, applies today&apos;s date math, and surfaces a list of <strong>Possible Hit numbers</strong> you can study and play with intention.
            </p>
            <p className="text-sm text-slate-400 print:text-slate-600">
              This guide walks you through all six steps — with a worked example you can follow along.
            </p>
          </CardContent>
        </Card>

        {/* STEP 1 */}
        <StepCard num={1} title="Choose Your Seed" icon={Target} testid="step-seed">
          <p>
            Your <strong>seed</strong> is where everything starts. It&apos;s a number you choose — a lucky date, a street number, last night&apos;s winning draw, or just a hunch.
          </p>
          <div className="mt-3 space-y-2 text-sm">
            <p><strong className="text-amber-300 print:text-amber-900">Pick 2</strong> games (daily 2-digit) → 2-digit seed, example: <span className="font-mono-digit">24</span></p>
            <p><strong className="text-amber-300 print:text-amber-900">Pick 3 / Cash 3</strong> games → 3-digit seed, example: <span className="font-mono-digit">245</span></p>
            <p><strong className="text-amber-300 print:text-amber-900">Pick 4 / Cash 4</strong> games → 4-digit seed, example: <span className="font-mono-digit">2450</span></p>
          </div>
          <p className="mt-3 text-sm text-slate-400 print:text-slate-600">
            For bigger games (Powerball, Mega Millions), use the 2-digit mode and run it per white-ball digit pair.
          </p>
        </StepCard>

        {/* STEP 2 — DATE SUM */}
        <StepCard num={2} title="Calculate the Date Sum" icon={Layers} testid="step-date-sum">
          <p>Take <strong>today&apos;s date</strong> and reduce it to a single digit using this rule:</p>
          <div className="mt-3 p-4 rounded-lg bg-slate-900/80 border border-slate-700 print:bg-white print:border-slate-400">
            <p className="font-heading text-amber-300 text-sm uppercase tracking-widest mb-3 print:text-amber-900">
              Formula
            </p>
            <p className="text-lg">
              <strong>Ones-of-month</strong> &nbsp;+&nbsp; <strong>Ones-of-day</strong> &nbsp;→&nbsp; reduce to a single digit
            </p>
          </div>

          <div className="mt-4 p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/30 print:bg-emerald-50 print:border-emerald-700">
            <p className="font-heading text-emerald-300 text-sm uppercase tracking-widest mb-3 print:text-emerald-900">
              Worked example — Oct 11, 2026
            </p>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <div className="flex flex-col items-center gap-1">
                <span className="text-[10px] uppercase text-slate-500">Month 10</span>
                <div className="flex gap-1"><Chip>1</Chip><Chip tone="amber">0</Chip></div>
              </div>
              <span className="text-slate-400 text-2xl font-mono-digit">+</span>
              <div className="flex flex-col items-center gap-1">
                <span className="text-[10px] uppercase text-slate-500">Day 11</span>
                <div className="flex gap-1"><Chip>1</Chip><Chip tone="amber">1</Chip></div>
              </div>
              <span className="text-slate-400 text-2xl font-mono-digit">=</span>
              <div className="flex flex-col items-center gap-1">
                <span className="text-[10px] uppercase text-slate-500">Raw</span>
                <Chip tone="cyan">1</Chip>
              </div>
              <span className="text-slate-400 text-2xl font-mono-digit">→</span>
              <div className="flex flex-col items-center gap-1">
                <span className="text-[10px] uppercase text-slate-500">Date Sum</span>
                <Chip tone="emerald">1</Chip>
              </div>
            </div>
            <p className="mt-3 text-sm text-slate-300 print:text-slate-700">
              Oct 11 → ones-of-month (0) + ones-of-day (1) = <strong>1</strong>. That&apos;s your Date Sum.
            </p>
          </div>
        </StepCard>

        {/* STEP 3 — MIRROR PAIRS */}
        <StepCard num={3} title="Find the Mirror" icon={Layers} testid="step-mirror">
          <p>Every digit has a <strong>mirror partner</strong>. Memorize these five pairs — they&apos;re the backbone of Hit Path:</p>
          <div className="mt-4 grid grid-cols-5 gap-2 sm:gap-3">
            {MIRROR_PAIRS.map((p) => (
              <div key={p.a} className="flex flex-col items-center gap-2 p-3 rounded-lg bg-slate-900/60 border border-slate-700 print:bg-white print:border-slate-400">
                <div className="flex items-center gap-1">
                  <Chip tone="emerald">{p.a}</Chip>
                  <span className="text-slate-500 font-mono-digit">↔</span>
                  <Chip tone="emerald">{p.b}</Chip>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4">
            For a Date Sum of <strong className="text-emerald-300 print:text-emerald-900">1</strong>, the mirror is <strong className="text-emerald-300 print:text-emerald-900">6</strong>. These two digits — <span className="font-mono-digit">1</span> and <span className="font-mono-digit">6</span> — are what we anchor the workout on.
          </p>
        </StepCard>

        {/* STEP 4 — ROLLING WORKOUT */}
        <StepCard num={4} title="Build the Rolling Workout" icon={Target} testid="step-rolling">
          <p>
            In the app, the seed anchors the <strong className="text-amber-300 print:text-amber-900">top and bottom row</strong> of each column.
            The digits in between roll <em>up</em> from 9 back to 0 — one step per row, each column rolling independently.
          </p>
          <p className="mt-3">On the grid you&apos;ll see two special markings:</p>
          <div className="mt-3 space-y-3">
            <div className="flex items-center gap-4 p-3 rounded-lg bg-slate-900/60 border border-slate-700 print:bg-white print:border-slate-400">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-emerald-500/20 border border-emerald-400 print:bg-emerald-50 print:border-emerald-700">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border-2 border-emerald-300 text-emerald-200 font-mono-digit text-2xl font-bold print:border-emerald-700 print:text-emerald-900">1</span>
              </span>
              <div>
                <p className="font-heading font-bold text-slate-100 print:text-slate-900">Circled digit = Date Sum</p>
                <p className="text-sm text-slate-400 print:text-slate-600">Every <span className="font-mono-digit">1</span> on the grid is circled.</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-3 rounded-lg bg-slate-900/60 border border-slate-700 print:bg-white print:border-slate-400">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-emerald-500/20 border border-emerald-400 print:bg-emerald-50 print:border-emerald-700">
                <span className="inline-block border-b-[3px] border-emerald-300 leading-none pb-0.5 text-emerald-200 font-mono-digit text-2xl font-bold print:border-emerald-700 print:text-emerald-900">6</span>
              </span>
              <div>
                <p className="font-heading font-bold text-slate-100 print:text-slate-900">Underlined digit = Mirror</p>
                <p className="text-sm text-slate-400 print:text-slate-600">Every <span className="font-mono-digit">6</span> on the grid is underlined.</p>
              </div>
            </div>
          </div>
        </StepCard>

        {/* STEP 5 — POSSIBLE HITS */}
        <StepCard num={5} title="Read the Possible Hits" icon={Sparkles} testid="step-hits">
          <p>
            Hit Path scans every <strong>row, column, and diagonal</strong> that passes through a circled or underlined cell — forward and reverse, straight line and box — and lists every <strong>Possible Hit number</strong> it finds.
          </p>
          <p className="mt-3">
            Each candidate is also run through <strong>mirror substitution</strong> — swapping one digit for its mirror partner — to surface close cousins you might otherwise miss.
          </p>
          <p className="mt-3 text-sm text-slate-400 print:text-slate-600">
            Star the ones that feel right to you. They save with your workout so you can check them against tomorrow&apos;s draw.
          </p>
        </StepCard>

        {/* STEP 6 — PRO FEATURES */}
        <StepCard num={6} title="Lucky Pick &amp; Draw Checker" icon={ShieldCheck} testid="step-pro">
          <p>Two Pro features speed up the workout once you&apos;re comfortable with the method:</p>
          <ul className="mt-3 space-y-2">
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5 print:text-emerald-700" />
              <span><strong className="text-slate-100 print:text-slate-900">Lucky Pick</strong> — one-click shortcut that picks a candidate for you based on today&apos;s math.</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5 print:text-emerald-700" />
              <span><strong className="text-slate-100 print:text-slate-900">Draw Checker</strong> — paste in last night&apos;s winning numbers; Hit Path tells you if the workout caught them.</span>
            </li>
          </ul>
          <p className="mt-3 text-sm text-slate-400 print:text-slate-600">
            Pro also unlocks daily email digests tuned to your seed — so your workout lands in your inbox every morning.
          </p>
        </StepCard>

        {/* STATE COVERAGE — print friendly block */}
        <Card className="bg-emerald-500/5 border-emerald-500/30 print:bg-emerald-50 print:border-emerald-700">
          <CardContent className="p-6 sm:p-8 space-y-3">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-emerald-300 print:text-emerald-900" />
              <h2 className="font-heading text-xl font-bold text-emerald-300 print:text-emerald-900">
                Works in Every State Lottery
              </h2>
            </div>
            <p className="text-slate-300 leading-relaxed print:text-slate-800">
              The Hit Path method is <strong>state-agnostic</strong>. If a game asks you to pick 2, 3, or 4 digits — or even a combination (Powerball, Mega Millions) — the same workout applies. New York, New Jersey, Florida, Texas, California, Georgia, Pennsylvania, Illinois, Ohio, Michigan, Virginia, Massachusetts, Washington, North Carolina, and every other US jurisdiction that runs daily digit games.
            </p>
          </CardContent>
        </Card>

        {/* LEAD CAPTURE — hidden on print */}
        <Card className="bg-gradient-to-br from-amber-500/10 to-slate-900/60 border-amber-500/40 print:hidden" data-testid="lead-capture-card">
          <CardContent className="p-6 sm:p-8 space-y-4">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-amber-300" />
              <h2 className="font-heading text-xl font-bold text-amber-300">Get the Pocket PDF</h2>
            </div>
            <p className="text-slate-300">
              Drop your email and we&apos;ll send you a printable version of this guide plus free workouts to try. No spam. Unsubscribe anytime.
            </p>
            {submitted ? (
              <div
                className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/40 text-emerald-200 font-heading font-bold"
                data-testid="lead-success"
              >
                You&apos;re on the list! Keep this page open and hit <strong>Save as PDF / Print</strong> up top to grab your copy now.
              </div>
            ) : (
              <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 bg-slate-900/70 border-slate-700 text-slate-100 placeholder:text-slate-500"
                  data-testid="lead-email-input"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  className="h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-heading font-bold px-6"
                  data-testid="lead-submit-btn"
                >
                  {loading ? "Sending…" : "Send me the PDF"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {/* CTA to try app — hidden on print */}
        <div className="text-center space-y-4 print:hidden">
          <h2 className="font-heading text-2xl font-bold text-slate-50">Ready to try it?</h2>
          <p className="text-slate-400 max-w-xl mx-auto">
            Open Hit Path, enter your seed, and let today&apos;s math do the rest.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/">
              <Button className="h-12 bg-amber-500 hover:bg-amber-400 text-slate-950 font-heading font-bold px-7" data-testid="open-app-btn">
                Open Hit Path
              </Button>
            </Link>
            <Link to="/upgrade">
              <Button variant="outline" className="h-12 border-amber-500/60 text-amber-300 hover:bg-amber-500/10 px-7" data-testid="see-pro-btn">
                See Pro Plans
              </Button>
            </Link>
          </div>
        </div>

        {/* FOOTER / LEGAL */}
        <footer className="pt-10 border-t border-slate-800 text-center space-y-2 print:border-slate-400">
          <p className="font-heading text-slate-300 print:text-slate-900">
            <strong>The Jersey Fellas LLC</strong> · thejerseyfellasllc.com
          </p>
          <p className="text-xs text-slate-500 max-w-2xl mx-auto print:text-slate-700">
            Hit Path is a workout tool for entertainment and study. It is <strong>not a prediction system</strong> and does not guarantee any outcome. Lottery games involve chance. Must be 18+ or the legal age in your jurisdiction. Play responsibly. If gambling is a problem for you or someone you know, call 1-800-GAMBLER.
          </p>
          <div className="flex justify-center gap-4 text-xs text-slate-500 pt-2 print:text-slate-700">
            <Link to="/terms" className="hover:text-amber-300 transition">Terms</Link>
            <span>·</span>
            <Link to="/privacy" className="hover:text-amber-300 transition">Privacy</Link>
          </div>
        </footer>
      </main>
    </div>
  );
}
